import React, { useState, useEffect } from 'react';

const AuthModal = ({ mode, onClose, onSuccess, onSwitchMode }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authMethod, setAuthMethod] = useState('email'); // email, wallet, magic_link, google
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');

  // Check if MetaMask is installed
  const isMetaMaskInstalled = () => {
    return typeof window !== 'undefined' && typeof window.ethereum !== 'undefined';
  };

  // Connect to specific wallet
  const connectWallet = async (walletType = 'metamask') => {
    if (walletType === 'metamask') {
      if (!isMetaMaskInstalled()) {
        setError('MetaMask não está instalado. Por favor, instale para continuar.');
        return;
      }

      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        if (accounts.length > 0) {
          const address = accounts[0];
          setWalletAddress(address);
          setWalletConnected(true);
          setError('');
          
          // For wallet auth, we don't need email/password
          setFormData({ ...formData, email: address.toLowerCase() });
        }
      } catch (error) {
        console.error('Erro ao conectar MetaMask:', error);
        setError('Erro ao conectar MetaMask: ' + error.message);
      }
    } else if (walletType === 'walletconnect') {
      // WalletConnect integration would go here
      setError('WalletConnect ainda não implementado. Use MetaMask por enquanto.');
    } else {
      setError('Tipo de carteira não suportado ainda.');
    }
  };

  // Wallet authentication
  const handleWalletAuth = async () => {
    if (!walletConnected) {
      await connectWallet();
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create a message to sign
      const message = `Login to Habeas Cripto\nAddress: ${walletAddress}\nTimestamp: ${Date.now()}`;
      
      // Request signature
      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [message, walletAddress]
      });

      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const payload = {
        wallet_address: walletAddress,
        signature: signature,
        message: message,
        auth_method: 'wallet',
        ...(mode === 'register' && { 
          name: formData.name || `User ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`,
          email: walletAddress.toLowerCase() + '@wallet.local'
        })
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erro na autenticação');
      }

      onSuccess(data.user, data.token);
    } catch (error) {
      console.error('Erro na autenticação wallet:', error);
      setError('Erro na autenticação: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Handle wallet authentication separately
    if (authMethod === 'wallet') {
      await handleWalletAuth();
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (mode === 'register' && formData.password !== formData.confirmPassword) {
        throw new Error('As senhas não coincidem');
      }

      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const payload = {
        email: formData.email,
        ...(mode === 'register' && { name: formData.name }),
        ...(authMethod === 'email' && { password: formData.password }),
        auth_method: authMethod
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erro na autenticação');
      }

      onSuccess(data.user, data.token);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleWalletConnect = async () => {
    try {
      if (typeof window.ethereum === 'undefined') {
        setError('MetaMask não está instalado. Por favor, instale o MetaMask para continuar.');
        return;
      }

      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });

      if (accounts.length > 0) {
        const walletAddress = accounts[0];
        
        // Try to register/login with wallet
        const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
        const payload = {
          email: `${walletAddress}@wallet.local`, // Temporary email for wallet users
          wallet_address: walletAddress,
          auth_method: 'wallet',
          ...(mode === 'register' && { name: `Usuário ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}` })
        };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || 'Erro na autenticação com carteira');
        }

        onSuccess(data.user, data.token);
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleMagicLink = async () => {
    if (!formData.email) {
      setError('Por favor, insira seu email para receber o link mágico');
      return;
    }

    setLoading(true);
    try {
      // Simulate magic link - in reality, this would send an email
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name || `Usuário ${formData.email.split('@')[0]}`,
          auth_method: 'magic_link'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erro ao enviar link mágico');
      }

      // For demo purposes, auto-login the user
      onSuccess(data.user, data.token);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="modal-overlay" 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="glass-card" 
        style={{
          width: '100%',
          maxWidth: '450px',
          padding: '2.5rem',
          position: 'relative'
        }}
        data-testid="auth-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'none',
            border: 'none',
            color: 'var(--silver-mist)',
            fontSize: '1.5rem',
            cursor: 'pointer',
            opacity: 0.7
          }}
          data-testid="close-auth-modal"
        >
          ×
        </button>

        <h2 style={{ textAlign: 'center', marginBottom: '2rem', color: 'var(--cyber-blue)' }} data-testid="auth-title">
          {mode === 'login' ? 'Entrar' : 'Criar Conta'}
        </h2>

        {/* Auth Method Selector */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              className={authMethod === 'email' ? 'btn-primary' : 'btn-ghost'}
              style={{ padding: '0.5rem 1rem', fontSize: '0.9rem', flex: 1, minWidth: '100px' }}
              onClick={() => setAuthMethod('email')}
              data-testid="auth-method-email"
            >
              📧 Email
            </button>
            <button
              type="button"
              className={authMethod === 'wallet' ? 'btn-primary' : 'btn-ghost'}
              style={{ padding: '0.5rem 1rem', fontSize: '0.9rem', flex: 1, minWidth: '100px' }}
              onClick={() => setAuthMethod('wallet')}
              data-testid="auth-method-wallet"
            >
              🦊 MetaMask
            </button>
            <button
              type="button"
              className={authMethod === 'magic_link' ? 'btn-primary' : 'btn-ghost'}
              style={{ padding: '0.5rem 1rem', fontSize: '0.9rem', flex: 1, minWidth: '100px' }}
              onClick={() => setAuthMethod('magic_link')}
              data-testid="auth-method-magic"
            >
              🔗 Link Mágico
            </button>
          </div>
        </div>

        {error && (
          <div 
            style={{
              background: 'rgba(255, 107, 53, 0.2)',
              color: 'var(--cyber-orange)',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}
            data-testid="auth-error"
          >
            {error}
          </div>
        )}

        {/* Wallet Connect */}
        {authMethod === 'wallet' && (
          <div style={{ textAlign: 'center' }}>
            {!isMetaMaskInstalled() ? (
              <div>
                <p style={{ marginBottom: '1rem', opacity: 0.8 }}>
                  MetaMask não está instalado
                </p>
                <a 
                  href="https://metamask.io/download/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-primary"
                  style={{ textDecoration: 'none', width: '100%', display: 'block', textAlign: 'center' }}
                >
                  🦊 Instalar MetaMask
                </a>
              </div>
            ) : walletConnected ? (
              <div>
                <p style={{ marginBottom: '1rem', opacity: 0.8 }}>
                  ✅ Carteira Conectada
                </p>
                <div style={{ 
                  background: 'rgba(255,255,255,0.1)', 
                  padding: '0.75rem', 
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  marginBottom: '1rem',
                  wordBreak: 'break-all'
                }}>
                  {walletAddress}
                </div>
                {mode === 'register' && (
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Nome (opcional)"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      style={{ width: '100%' }}
                    />
                  </div>
                )}
                <button
                  className="btn-primary"
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ width: '100%' }}
                  data-testid="wallet-auth-btn"
                >
                  {loading ? <span className="loading"></span> : `${mode === 'login' ? 'Entrar' : 'Criar Conta'} com Wallet`}
                </button>
              </div>
            ) : (
              <div>
                <p style={{ marginBottom: '2rem', opacity: 0.8 }}>
                  Escolha uma carteira para conectar:
                </p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <button
                    className="btn-primary"
                    onClick={() => connectWallet('metamask')}
                    disabled={loading}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                    data-testid="connect-metamask-btn"
                  >
                    {loading ? <span className="loading"></span> : (
                      <>
                        🦊 MetaMask
                        {isMetaMaskInstalled() ? (
                          <span style={{ fontSize: '0.8rem', opacity: 0.8 }}> - Detectado</span>
                        ) : (
                          <span style={{ fontSize: '0.8rem', opacity: 0.8 }}> - Não instalado</span>
                        )}
                      </>
                    )}
                  </button>
                  
                  <button
                    className="btn-ghost"
                    onClick={() => connectWallet('walletconnect')}
                    disabled={loading}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                    data-testid="connect-walletconnect-btn"
                  >
                    📱 WalletConnect <span style={{ fontSize: '0.8rem', opacity: 0.8 }}> - Em breve</span>
                  </button>
                </div>

                {!isMetaMaskInstalled() && (
                  <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                    <a 
                      href="https://metamask.io/download/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-ghost"
                      style={{ textDecoration: 'none', fontSize: '0.9rem' }}
                    >
                      📥 Instalar MetaMask
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Magic Link */}
        {authMethod === 'magic_link' && (
          <form onSubmit={(e) => { e.preventDefault(); handleMagicLink(); }}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="seu@email.com"
                required
                data-testid="magic-link-email"
              />
            </div>
            {mode === 'register' && (
              <div className="form-group">
                <label className="form-label">Nome Completo</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Seu nome completo"
                  required
                  data-testid="magic-link-name"
                />
              </div>
            )}
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
              style={{ width: '100%' }}
              data-testid="send-magic-link-btn"
            >
              {loading ? <span className="loading"></span> : 'Enviar Link Mágico'}
            </button>
          </form>
        )}

        {/* Email & Password Form */}
        {authMethod === 'email' && (
          <form onSubmit={handleSubmit}>
            {mode === 'register' && (
              <div className="form-group">
                <label className="form-label">Nome Completo</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Seu nome completo"
                  required
                  data-testid="register-name-input"
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="seu@email.com"
                required
                data-testid="email-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Senha</label>
              <input
                type="password"
                className="form-input"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="Sua senha"
                required
                data-testid="password-input"
              />
            </div>

            {mode === 'register' && (
              <div className="form-group">
                <label className="form-label">Confirmar Senha</label>
                <input
                  type="password"
                  className="form-input"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  placeholder="Confirme sua senha"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            )}

            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
              style={{ width: '100%' }}
              data-testid="submit-auth-btn"
            >
              {loading ? <span className="loading"></span> : (mode === 'login' ? 'Entrar' : 'Criar Conta')}
            </button>
          </form>
        )}

        {/* Switch Mode */}
        <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <span style={{ opacity: 0.7 }}>
            {mode === 'login' ? 'Não tem conta?' : 'Já tem conta?'}
          </span>
          <button
            type="button"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--cyber-blue)',
              marginLeft: '0.5rem',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            onClick={() => onSwitchMode(mode === 'login' ? 'register' : 'login')}
            data-testid="switch-auth-mode-btn"
          >
            {mode === 'login' ? 'Cadastre-se' : 'Faça login'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;